import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type {
  CreateLabTestRequestT,
  LabTestCollectionMethodT,
  LabTestTVitalLabTestMetadataTVitalLabMarkerMetadataT as LabTestT,
  SubmitLabOrderRequestT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { Button } from '@/shared/ui/shadcn/button';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { useCopilotOverlays } from '@/shared/stores/ui';
import type { ICD10CodeT } from '@/entities/labs';
import { analytics, ANALYTICS_EVENTS, isRevisionFlow } from '@/shared/lib';

import type {
  CustomTestLabOrderDraft,
  ExistingTestLabOrderDraft,
  LabOrderDraft,
  LabOrderWizardStepId,
} from '../model/types.js';
import { INITIAL_EXISTING_TEST_DRAFT, INITIAL_CUSTOM_TEST_DRAFT } from '../model/types.js';
import { validateDraft } from '../model/schema.js';
import { useMarkerSelection } from '../model/useMarkerSelection.js';
import { useLabOrderSubmissionOrchestrator } from '../model/submission/orchestrator.js';
import type { ExistingLabOrderWizardPrefill } from '../model/labOrderWizardPrefill.js';
import { useResolveLabTest } from '../model/useResolveLabTest.js';
import { useLabOrderFinalization } from '../model/useLabOrderFinalization.js';
import { toAoeAnswersArray, areRequiredQuestionsAnswered } from '../model/aoeHelpers.js';

import { LabOrderWizardProgress } from './LabOrderWizardProgress.js';
import { SelectTestStep } from './steps/SelectTestStep.js';
import { CustomTestStep } from './steps/CustomTestStep.js';
import { DiseaseCodesStep } from './steps/DiseaseCodesStep.js';
import { ReviewStep } from './steps/ReviewStep.js';
import { ConfirmationStep } from './steps/ConfirmationStep.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildOrderData(
  draft: LabOrderDraft
): Omit<SubmitLabOrderRequestT, 'labTestId'> & { icdCodes?: string[] } {
  return {
    aoeAnswers: toAoeAnswersArray(draft.aoeAnswersById),
    notes: draft.notes.trim() || undefined,
    priority: draft.priority || undefined,
    icdCodes: draft.icdCodes.length > 0 ? draft.icdCodes.map((c) => c.code) : undefined,
  };
}

function buildTestData(draft: CustomTestLabOrderDraft): CreateLabTestRequestT {
  return {
    name: draft.name,
    description: draft.description,
    method: draft.method,
    markerIds: draft.selectedMarkers.map((m) => m.id),
  };
}

interface StepMeta {
  title: string;
  subtitle?: string;
}

function getStepMeta(
  stepId: LabOrderWizardStepId,
  labName: string | undefined,
  isPrefilledOrder: boolean
): StepMeta {
  switch (stepId) {
    case 'select-test':
      return {
        title: 'Choose a lab test',
        subtitle:
          'Pre-configured panels curated by our clinical team, each covering one or more biomarkers.',
      };
    case 'custom-test':
      return {
        title: 'Build a custom panel',
        subtitle: 'Select individual biomarkers to create a new lab test.',
      };
    case 'disease-codes':
      if (isPrefilledOrder) {
        return {
          title: 'Add diagnoses for this order',
          subtitle: 'These ICD-10 codes will be included on the shared Quest requisition.',
        };
      }
      return {
        title: labName ? `Add diagnosis codes for ${labName}` : 'Add diagnosis codes',
        subtitle:
          'ICD-10 codes establish medical necessity and are required for insurance billing.',
      };
    case 'review':
      return {
        title: 'Review & submit',
        subtitle: 'Confirm the details below, then submit your order.',
      };
    case 'confirmation':
      return { title: 'Order submitted' };
    default:
      return { title: '' };
  }
}

// ---------------------------------------------------------------------------
// Wizard
// ---------------------------------------------------------------------------

interface LabOrderWizardProps {
  userId: string;
  prefill: ExistingLabOrderWizardPrefill | null;
}

export function LegacyLabOrderWizard({ userId, prefill }: LabOrderWizardProps) {
  const overlays = useCopilotOverlays();
  const initialPrefill = prefill;
  const finalization = useLabOrderFinalization({
    userId,
    sourceProposedAction: initialPrefill?.sourceProposedAction,
  });

  const [stepId, setStepId] = useState<LabOrderWizardStepId>(
    !initialPrefill
      ? 'select-test'
      : isRevisionFlow(initialPrefill.sourceProposedAction)
        ? 'disease-codes'
        : 'review'
  );
  const [draft, setDraft] = useState<LabOrderDraft>(() =>
    initialPrefill
      ? {
          ...INITIAL_EXISTING_TEST_DRAFT,
          labTestId: initialPrefill.labTestId,
          labTestName: initialPrefill.labTestName,
          icdCodes: initialPrefill.icdCodes ?? [],
          notes: initialPrefill.notes ?? '',
          priority: initialPrefill.priority ?? false,
        }
      : INITIAL_EXISTING_TEST_DRAFT
  );
  const [formError, setFormError] = useState<string | null>(null);

  const markerSelection = useMarkerSelection();
  const orchestrator = useLabOrderSubmissionOrchestrator();

  // Resolve the full lab test (with aoe_schema) when opening via prefill.
  // The prefill only carries an ID; the DiseaseCodesStep needs the full
  // object to render AOE questions the clinician must answer before submit.
  const { labTest: resolvedLabTest, isLoading: isResolvingLabTest } = useResolveLabTest(
    initialPrefill?.labTestId
  );

  // Prefer the draft's own selectedLabTest (set when the user picks from
  // search) and fall back to the prefill-resolved one.
  const effectiveSelectedLabTest =
    draft.mode === 'existing_test' ? (draft.selectedLabTest ?? resolvedLabTest) : undefined;

  const { state: submissionState, isDraftLocked: isLegacyDraftLocked, isSubmitting } = orchestrator;
  const isDraftLocked = finalization.submittedOrder !== null || isLegacyDraftLocked;
  const [isEditableMode, setIsEditableMode] = useState(
    !finalization.submittedOrder &&
      (!initialPrefill?.sourceProposedAction || isRevisionFlow(initialPrefill.sourceProposedAction))
  );

  useEffect(() => {
    if (!initialPrefill || isDraftLocked) {
      return;
    }
    orchestrator.reset();
  }, [initialPrefill, isDraftLocked, orchestrator]);

  const analyticsContext = useMemo(
    () => ({ actor: 'clinician' as const, patientUserId: userId }),
    [userId]
  );

  const wizardOpenedRef = useRef(false);
  useEffect(() => {
    if (wizardOpenedRef.current) {
      return;
    }
    wizardOpenedRef.current = true;
    void analytics.track({
      name: ANALYTICS_EVENTS.labs.wizardOpened,
      context: analyticsContext,
    });
  }, [analyticsContext]);

  const prevStepRef = useRef(stepId);
  useEffect(() => {
    if (prevStepRef.current === stepId) {
      return;
    }
    prevStepRef.current = stepId;
    void analytics.track({
      name: ANALYTICS_EVENTS.labs.wizardStepViewed,
      context: analyticsContext,
      properties: { stepId },
    });
  }, [stepId, analyticsContext]);

  const labName = useMemo(() => {
    if (draft.mode === 'existing_test') {
      return draft.labTestName;
    }
    if (draft.mode === 'custom_test' && draft.name.trim()) {
      return draft.name.trim();
    }
    return undefined;
  }, [draft]);

  const stepMeta = getStepMeta(stepId, labName, initialPrefill !== null);

  // -- Step that precedes disease-codes (depends on flow) --
  const stepBeforeCodes = draft.mode === 'custom_test' ? 'custom-test' : 'select-test';

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleSelectLabTest = (labTest: LabTestT) => {
    setDraft({
      ...INITIAL_EXISTING_TEST_DRAFT,
      labTestId: labTest.id,
      labTestName: labTest.name,
      selectedLabTest: labTest,
    });
    setFormError(null);
    void analytics.track({
      name: ANALYTICS_EVENTS.labs.wizardModeSelected,
      context: analyticsContext,
      properties: { mode: 'existing_test' },
    });
    setStepId('disease-codes');
  };

  const handleCreateNew = () => {
    orchestrator.reset();
    markerSelection.clear();
    setFormError(null);
    setDraft(INITIAL_CUSTOM_TEST_DRAFT);
    void analytics.track({
      name: ANALYTICS_EVENTS.labs.wizardModeSelected,
      context: analyticsContext,
      properties: { mode: 'custom_test' },
    });
    setStepId('custom-test');
  };

  const updateDraft = (
    updates: Partial<ExistingTestLabOrderDraft> | Partial<CustomTestLabOrderDraft>
  ) => {
    setDraft((d) => ({ ...d, ...updates }) as LabOrderDraft);
  };

  const handleAddIcdCode = useCallback((code: ICD10CodeT) => {
    setDraft((d) => {
      if (d.icdCodes.some((c) => c.code === code.code)) {
        return d;
      }
      return { ...d, icdCodes: [...d.icdCodes, code] } as LabOrderDraft;
    });
  }, []);

  const handleRemoveIcdCode = useCallback((code: string) => {
    setDraft(
      (d) =>
        ({
          ...d,
          icdCodes: d.icdCodes.filter((c) => c.code !== code),
        }) as LabOrderDraft
    );
  }, []);

  // ---------------------------------------------------------------------------
  // Validation & navigation
  // ---------------------------------------------------------------------------

  const validateAndProceedToReview = () => {
    setFormError(null);

    if (draft.icdCodes.length === 0) {
      setFormError('Please add at least one ICD-10 diagnosis code.');
      return;
    }

    if (draft.mode === 'existing_test') {
      if (!draft.labTestId) {
        setFormError('Please select a lab test.');
        return;
      }
      if (effectiveSelectedLabTest) {
        const hasRequired = effectiveSelectedLabTest.aoe_schema.sections.some((s) =>
          s.questions.some((q) => q.body.required)
        );
        if (
          hasRequired &&
          !areRequiredQuestionsAnswered(effectiveSelectedLabTest.aoe_schema, draft.aoeAnswersById)
        ) {
          setFormError('Please answer all required questions before continuing.');
          return;
        }
      }
      const result = validateDraft(draft);
      if (!result.success) {
        setFormError(result.error.issues[0]?.message ?? 'Please fix validation errors.');
        return;
      }
    } else if (draft.mode === 'custom_test') {
      const syncedDraft: CustomTestLabOrderDraft = {
        ...draft,
        selectedMarkers: markerSelection.selectedMarkers,
      };
      setDraft(syncedDraft);
      const result = validateDraft(syncedDraft);
      if (!result.success) {
        setFormError(result.error.issues[0]?.message ?? 'Please fix validation errors.');
        return;
      }
    }

    setStepId('review');
  };

  const validateCustomAndProceedToCodes = () => {
    setFormError(null);
    const syncedDraft: CustomTestLabOrderDraft = {
      ...(draft as CustomTestLabOrderDraft),
      selectedMarkers: markerSelection.selectedMarkers,
    };
    setDraft(syncedDraft);

    if (!syncedDraft.name.trim()) {
      setFormError('Please enter a test name.');
      return;
    }
    if (!syncedDraft.description.trim()) {
      setFormError('Please enter a description.');
      return;
    }
    if (syncedDraft.selectedMarkers.length === 0) {
      setFormError('Please add at least one biomarker.');
      return;
    }

    setStepId('disease-codes');
  };

  const handleBack = () => {
    setFormError(null);

    switch (stepId) {
      case 'confirmation':
        if (submissionState.status !== 'success') {
          setStepId('review');
        }
        break;
      case 'review':
        setStepId('disease-codes');
        break;
      case 'disease-codes':
        setStepId(stepBeforeCodes);
        break;
      case 'custom-test':
        setStepId('select-test');
        break;
      default:
        break;
    }
  };

  // ---------------------------------------------------------------------------
  // Submission
  // ---------------------------------------------------------------------------

  const handleSubmit = async () => {
    setFormError(null);

    if (finalization.submittedOrder) {
      if (await finalization.retry()) {
        overlays.close('labOrderWizard');
      }
      return;
    }

    if (draft.mode === 'existing_test') {
      if (effectiveSelectedLabTest) {
        const hasRequired = effectiveSelectedLabTest.aoe_schema.sections.some((s) =>
          s.questions.some((q) => q.body.required)
        );
        if (
          hasRequired &&
          !areRequiredQuestionsAnswered(effectiveSelectedLabTest.aoe_schema, draft.aoeAnswersById)
        ) {
          setIsEditableMode(true);
          setFormError(
            'This test requires answers to additional questions before it can be submitted.'
          );
          setStepId('disease-codes');
          return;
        }
      }
    }

    void analytics.track({
      name: ANALYTICS_EVENTS.labs.orderSubmitAttempted,
      context: analyticsContext,
      properties: {
        mode: draft.mode,
        hasNotes: draft.notes.trim().length > 0,
        notesLength: draft.notes.trim().length > 0 ? draft.notes.trim().length : undefined,
        aoeAnswerCount: Object.keys(draft.aoeAnswersById).length,
        prioritySet: draft.priority,
      },
    });

    let result: { remoteOrderId: string; labTestId?: string } | null = null;

    if (draft.mode === 'custom_test') {
      const finalDraft: CustomTestLabOrderDraft = {
        ...draft,
        selectedMarkers: markerSelection.selectedMarkers,
      };
      result = await orchestrator.createTestAndSubmitOrder({
        userId,
        testData: buildTestData(finalDraft),
        orderData: buildOrderData(finalDraft),
      });

      const createOutcome =
        orchestrator.state.status === 'failed-create-test' ? 'failure' : 'success';
      void analytics.track({
        name: ANALYTICS_EVENTS.labs.testCreateResult,
        context: analyticsContext,
        properties: { outcome: createOutcome },
      });
    } else {
      if (!draft.labTestId) {
        setFormError('No lab test selected.');
        return;
      }
      result = await orchestrator.submitOrder({
        userId,
        labTestId: draft.labTestId,
        data: buildOrderData(draft),
      });
    }

    const submitOutcome = result ? 'success' : 'failure';
    void analytics.track({
      name: ANALYTICS_EVENTS.labs.orderSubmitResult,
      context: analyticsContext,
      properties: { mode: draft.mode, outcome: submitOutcome },
    });

    if (result) {
      if (await finalization.finalize(result)) {
        overlays.close('labOrderWizard');
      }
    }
  };

  const handleRetryCreate = async () => {
    if (draft.mode !== 'custom_test') {
      return;
    }
    const created = await orchestrator.createTest({ data: buildTestData(draft) });
    if (created) {
      const submitted = await orchestrator.submitOrder({
        userId,
        labTestId: created.labTestId,
        data: buildOrderData(draft),
      });
      if (submitted) {
        if (await finalization.finalize(submitted)) {
          overlays.close('labOrderWizard');
        }
      }
    }
  };

  const handleRetrySubmit = async () => {
    const labTestId =
      submissionState.labTestId ?? (draft.mode === 'existing_test' ? draft.labTestId : null);
    if (!labTestId) {
      return;
    }
    const submitted = await orchestrator.submitOrder({
      userId,
      labTestId,
      data: buildOrderData(draft),
    });
    if (submitted) {
      if (await finalization.finalize(submitted)) {
        overlays.close('labOrderWizard');
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Derived UI state
  // ---------------------------------------------------------------------------

  const backHidden = stepId === 'select-test' || (!!initialPrefill && stepId === 'disease-codes');
  const backDisabled =
    stepId === 'confirmation' && submissionState.status === 'success'
      ? true
      : !isEditableMode && stepId === 'review';

  const reviewDraft: LabOrderDraft =
    draft.mode === 'custom_test'
      ? { ...draft, selectedMarkers: markerSelection.selectedMarkers }
      : effectiveSelectedLabTest
        ? { ...draft, selectedLabTest: effectiveSelectedLabTest }
        : draft;

  const submitLabel = finalization.isFinalizing
    ? 'Updating action…'
    : finalization.submittedOrder
      ? 'Retry action update'
      : submissionState.status === 'creating-test'
        ? 'Creating…'
        : submissionState.status === 'submitting-order'
          ? 'Submitting…'
          : submissionState.status === 'failed-create-test'
            ? 'Retry create'
            : isDraftLocked
              ? 'Created'
              : draft.mode === 'custom_test'
                ? 'Create & submit'
                : 'Submit order';

  const nextDisabled =
    stepId === 'custom-test' &&
    draft.mode === 'custom_test' &&
    markerSelection.selectedMarkers.length === 0;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="w-full max-w-3xl">
      {isEditableMode ? (
        <>
          <LabOrderWizardProgress stepId={stepId} mode={draft.mode} />

          <div className="mt-4">
            <h2 className="text-md font-medium leading-none tracking-tight">{stepMeta.title}</h2>
            {stepMeta.subtitle && (
              <p className="mt-1.5 text-sm text-muted-foreground">{stepMeta.subtitle}</p>
            )}
          </div>
        </>
      ) : null}

      <div className="mt-6 space-y-6">
        {(formError ?? finalization.errorMessage) && (
          <Alert variant="destructive">
            <AlertTitle>Can&rsquo;t continue</AlertTitle>
            <AlertDescription>{formError ?? finalization.errorMessage}</AlertDescription>
          </Alert>
        )}

        {stepId === 'select-test' && (
          <SelectTestStep onSelectLabTest={handleSelectLabTest} onCreateNew={handleCreateNew} />
        )}

        {stepId === 'custom-test' && draft.mode === 'custom_test' && (
          <CustomTestStep
            name={draft.name}
            onNameChange={(n) => updateDraft({ name: n })}
            description={draft.description}
            onDescriptionChange={(d) => updateDraft({ description: d })}
            method={draft.method}
            onMethodChange={(m: LabTestCollectionMethodT) => updateDraft({ method: m })}
            markerSelection={markerSelection}
          />
        )}

        {stepId === 'disease-codes' && (
          <DiseaseCodesStep
            aoeSchema={effectiveSelectedLabTest?.aoe_schema}
            isLoadingLabTest={isResolvingLabTest}
            aoeAnswersById={draft.aoeAnswersById}
            onAoeChange={(a) => updateDraft({ aoeAnswersById: a })}
            notes={draft.notes}
            onNotesChange={(n) => updateDraft({ notes: n })}
            priority={draft.priority}
            onPriorityChange={(p) => updateDraft({ priority: p })}
            icdCodes={draft.icdCodes}
            onAddIcdCode={handleAddIcdCode}
            onRemoveIcdCode={handleRemoveIcdCode}
          />
        )}

        {stepId === 'review' && (
          <ReviewStep
            draft={reviewDraft}
            errorMessage={submissionState.errorMessage}
            onEditDetails={isEditableMode ? () => setStepId('disease-codes') : undefined}
          />
        )}

        {stepId === 'confirmation' && (
          <ConfirmationStep
            submissionState={submissionState}
            onRetryCreateTest={handleRetryCreate}
            onRetrySubmitOrder={handleRetrySubmit}
          />
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        {!backHidden && (
          <Button
            type="button"
            variant="secondary"
            radius="pill"
            onClick={handleBack}
            disabled={backDisabled}
          >
            Back
          </Button>
        )}

        <div className="flex gap-2">
          {stepId === 'custom-test' && (
            <Button
              type="button"
              radius="pill"
              onClick={validateCustomAndProceedToCodes}
              disabled={nextDisabled}
            >
              Next
            </Button>
          )}

          {stepId === 'disease-codes' && (
            <Button type="button" radius="pill" onClick={validateAndProceedToReview}>
              Next
            </Button>
          )}

          {stepId === 'review' && (
            <Button
              type="button"
              radius="pill"
              onClick={() => void handleSubmit()}
              disabled={
                isSubmitting ||
                finalization.isFinalizing ||
                (!finalization.submittedOrder && (isDraftLocked || isResolvingLabTest))
              }
            >
              {submitLabel}
            </Button>
          )}

          {stepId === 'confirmation' && (
            <Button
              type="button"
              radius="pill"
              onClick={() => overlays.close()}
              disabled={isSubmitting}
            >
              Finish
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
